
/**
 * Module dependencies.
 */

import { ComponentType, ForwardedRef, MouseEvent, forwardRef } from 'react';
import { Icon } from 'react-components/media';
import { Loading, RouterLink, typography } from '../design-system';
import { color } from 'react-components/styles';
import { ifProp, switchProp, theme } from 'styled-tools';
import styled, { css } from 'styled-components';

/**
 * `ButtonProps` type.
 */

type ButtonProps = {
  as?: string | ComponentType<any>;
  colorTheme: 'discord' | 'email' | 'facebook' | 'google' | 'twitter';
  disabled?: boolean;
  fullWidth?: boolean;
  href?: string;
  icon: any;
  isLoading?: boolean;
  label?: string;
  onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  size?: 'large';
  target?: string;
  to?: string | {
    pathname: string;
    state: any;
  };
  type?: 'button' | 'submit';
};

/**
 * `StyledIcon` styled component.
 */

const StyledIcon = styled(Icon)`
  transition: color ${theme('animations.defaultTransition')};
`;

/**
 * `BaseButton` styled component.
 */

const BaseButton = styled.button.attrs<ButtonProps>(({ as, href, to, type }: any) => {
  const element = as || to && RouterLink || href && 'a' || 'button';

  return {
    as: element,
    type: type || (element === 'button' ? 'button' : null)
  };
})`
  align-items: center;
  appearance: none;
  background-color: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  font-family: ${typography.fontFamilies.saira};
  justify-content: center;
  outline: none;
  position: relative;
  text-decoration: none;
  transition: ${theme('animations.defaultTransition')};
  transition-property: background-color, box-shadow;
  width: max-content;

  ${switchProp('colorTheme', {
    discord: css`
      background-color: rgba(88, 101, 242, 1);
      color: ${color('white')};

      &:hover {
        background-color: rgba(88, 101, 242, 0.95);
      }
    `,
    email: css`
      background-color: ${color('white')};
      border-color: ${color('gray400')};
      color: ${color('gray800')};

      ${StyledIcon} {
        color: ${color('gray500')};
      }

      &:hover {
        ${StyledIcon} {
          color: ${color('gray600')};
        }
      }
    `,
    facebook: css`
      background-color: ${color('facebook')};
      color: ${color('white')};

      &:hover {
        background-color: ${color.transparentize('facebook', 0.95)};
      }
    `,
    google: css`
      background-color: ${color('white')};
      border-color: ${color('gray400')};
      color: ${color('gray800')};

      &:hover {
        background-color: rgba(111, 121, 121, 0.08);
      }
    `,
    twitter: css`
      background-color: rgba(0, 0, 0, 1);
      color: ${color('white')};

      &:hover {
        background-color: rgba(0, 0, 0, 0.95);
      }
    `
  })}

  ${switchProp('size', {
    large: css`
      font-size: 18px;
      font-weight: 400;
      letter-spacing: -0.2px;
      line-height: 24px;
    `
  })};

  ${ifProp('hasLabel', css`
    padding: 9px 15px;
  `, css`
    padding: 9px;
  `)}

  ${ifProp('fullWidth', css`
    width: 100%;
  `)}

  &:focus-visible {
    box-shadow:
      0 0 0 2px ${color('white')}, 
      0 0 0 4px ${color('indigo500')};
  }

  &:disabled,
  &[aria-disabled='true'] {
    cursor: default;
    pointer-events: none;
  }
`;

/**
 * `Content` styled component.
 */

const Content = styled.div<{ isVisible?: boolean }>`
  align-items: center;
  gap: 12px;
  display: flex;
  justify-content: center;
  opacity: ${ifProp('isVisible', 1, 0)};
  transition: opacity ${theme('animations.defaultTransition')};
`;

/**
 * `Button` component.
 */

function Button(props: ButtonProps, ref: ForwardedRef<HTMLButtonElement>): JSX.Element {
  const { colorTheme, disabled, icon, isLoading, label, size = 'large', ...rest } = props;

  return (
    <BaseButton
      {...rest}
      aria-disabled={!!disabled || isLoading}
      colorTheme={colorTheme}
      disabled={disabled || isLoading}
      hasLabel={!!label}
      ref={ref}
      size={size}
    >
      <Content isVisible={!isLoading}>
        <StyledIcon
          aria-hidden
          icon={icon}
          size={'24px'}
        />

        {label}
      </Content>

      <Loading active={!!isLoading} />
    </BaseButton>
  );
}

/**
 * Forwarded `Button`.
 */

const ForwardedButton = forwardRef<HTMLButtonElement, ButtonProps>(Button);

/**
 * Export `Button` component.
 */

export { ForwardedButton as Button };
